import { useEffect, useRef } from 'react';
import type { MascotFrameAnimation } from './mascotData';
import './MascotFramePlayer.css';

interface MascotFramePlayerProps {
  animation: MascotFrameAnimation;
  className?: string;
  glow?: boolean;
  onComplete?: () => void;
}

export default function MascotFramePlayer({ animation, className = '', glow = true, onComplete }: MascotFramePlayerProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const onCompleteRef = useRef(onComplete);

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let frame = 0;
    let raf = 0;
    let last = performance.now();
    let cancelled = false;
    let completed = false;
    const image = new Image();
    image.decoding = 'async';
    image.src = animation.spriteSrc;

    const drawFrame = () => {
      if (!ctx || !canvas || !image.complete) return;
      const col = frame % animation.columns;
      const row = Math.floor(frame / animation.columns);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(
        image,
        col * animation.frameWidth,
        row * animation.frameHeight,
        animation.frameWidth,
        animation.frameHeight,
        0,
        0,
        canvas.width,
        canvas.height,
      );
    };

    const tick = (now: number) => {
      if (cancelled) return;
      const interval = 1000 / animation.fps;
      if (now - last >= interval) {
        last = now;
        if (animation.loop) {
          frame = (frame + 1) % animation.totalFrames;
        } else if (frame < animation.totalFrames - 1) {
          frame += 1;
        } else if (!completed) {
          completed = true;
          onCompleteRef.current?.();
        }
        drawFrame();
      }
      if (animation.loop || !completed) raf = requestAnimationFrame(tick);
    };

    image.onload = () => {
      frame = 0;
      drawFrame();
      raf = requestAnimationFrame(tick);
    };
    image.onerror = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    };

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
    };
  }, [animation]);

  return (
    <div className={`adc-mascot-frame-player ${glow ? 'adc-mascot-frame-glow' : ''} ${className}`} aria-hidden="true">
      <canvas ref={canvasRef} width={animation.frameWidth} height={animation.frameHeight} />
    </div>
  );
}
